import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchMtrStructTree } from '#/api/imsMgmt/settings/mtrStruct/fetchTree';
import { fetchMtrSpecList } from '#/api/imsMgmt/settings/mtrSpec/fetchList';
import {
  MTR_TEMPLATE_DEFAULT_VALUES,
} from '#/modules/imsMgmt/settings/mtrTemplate/schemas/mtrTemplateSchema';
import {
  fromTree,
  resolveMainIdBySubId,
} from '#/modules/imsMgmt/settings/mtrStruct/helper/structFilterModel';
import { mtrSpecListQueryKey, mtrStructTreeQueryKey } from '#/utils/queryKeys';

const SPEC_LOOKUP_QUERY = {
  page: 1,
  pageSize: 200,
  keyword: '',
  sort: [],
  filters: { status: 'ACTIVE' },
};

export function mapMtrTemplateDetailToForm(detail, treeRows = []) {
  if (!detail) {
    return MTR_TEMPLATE_DEFAULT_VALUES;
  }

  const model = fromTree(treeRows);
  const mtrStructSubId = detail.mtrStructSub?.id ?? detail.mtrStructSubId ?? '';
  const mtrStructMainId = resolveMainIdBySubId(model, mtrStructSubId);

  return {
    mtrStructMainId,
    mtrStructSubId,
    code: detail.code ?? '',
    name: detail.name ?? '',
    remark: detail.remark ?? '',
    serialFormatter: detail.serialFormatter ?? '',
    specs: Array.isArray(detail.specs)
      ? detail.specs.map((rule) => ({
        id: rule.id,
        mtrSpecId: rule.mtrSpec?.id ?? rule.mtrSpecId ?? '',
        required: Boolean(rule.required),
        displayOrder: rule.displayOrder ?? 0,
      }))
      : [],
  };
}

export function buildMtrTemplateMutationPayload(values, updatedAt = null) {
  const specs = values.specs.map((rule) => {
    const item = {
      mtrSpecId: rule.mtrSpecId,
      required: Boolean(rule.required),
      displayOrder: Number(rule.displayOrder),
    };

    if (rule.id) {
      item.id = rule.id;
    }

    return item;
  });

  const payload = {
    mtrStructSubId: values.mtrStructSubId,
    code: values.code,
    name: values.name,
    remark: values.remark || null,
    serialFormatter: values.serialFormatter,
    specs,
  };

  if (updatedAt) {
    payload.updatedAt = updatedAt;
  }

  return payload;
}

function useMtrTemplateForm(mtrStructMainId = '') {
  const treeQuery = useQuery({
    queryKey: mtrStructTreeQueryKey(),
    queryFn: () => fetchMtrStructTree({ status: 'ACTIVE' }),
  });

  const specQuery = useQuery({
    queryKey: mtrSpecListQueryKey(SPEC_LOOKUP_QUERY),
    queryFn: () => fetchMtrSpecList(SPEC_LOOKUP_QUERY),
  });

  const treeRows = useMemo(
    () => treeQuery.data?.data ?? [],
    [treeQuery.data?.data],
  );

  const structMainOptions = useMemo(() => {
    return fromTree(treeRows).mains;
  }, [treeRows]);

  const structSubOptions = useMemo(() => {
    if (!mtrStructMainId) {
      return [];
    }

    const model = fromTree(treeRows);
    const group = model.subGroups.find((item) => item.mainId === mtrStructMainId);

    return (group?.subs ?? []).map((sub) => ({
      value: sub.value,
      label: sub.label,
    }));
  }, [treeRows, mtrStructMainId]);

  const specOptions = useMemo(() => (
    (specQuery.data?.data ?? []).map((spec) => ({
      value: spec.id,
      label: `${spec.code} - ${spec.name}`,
      code: spec.code,
      name: spec.name,
      specType: spec.specType,
    }))
  ), [specQuery.data?.data]);

  return {
    structMainOptions,
    structSubOptions,
    treeRows,
    specOptions,
    isOptionsLoading: treeQuery.isLoading || specQuery.isLoading,
  };
}

export default useMtrTemplateForm;
